import { useState } from 'react';
import { Play, Square, Loader2 } from 'lucide-react';
import { useActiveSession, useStartSession, useStopSession, useWallet } from '@/lib/walletHooks';
import { instanceCardState, startSessionErrorMessage } from '@/lib/instanceState';
import { formatCurrencyEurRo } from '@/lib/format';
import { tierLabel } from '@/lib/tiers';
import { useMe } from '@/lib/useMe';
import { canSeeBilling } from '@/lib/permissions';
import { Button } from '@/components/ui/button';
import { Pill } from '@/components/ui/Pill';

export function Instanta() {
  const { data: active, isPending, isError } = useActiveSession();
  const { data: w } = useWallet();
  const { data: me } = useMe();
  const billing = canSeeBilling(me); // only admins/operators see EUR; members get qualitative
  const start = useStartSession();
  const stop = useStopSession();
  const [startError, setStartError] = useState<string | null>(null);

  async function onStart() {
    setStartError(null);
    try { await start.mutateAsync(); }
    catch (e) {
      setStartError(startSessionErrorMessage(e));
      // 409 (a colleague started/stopped concurrently) maps to `null` above
      // (nothing shown) — it's corrected by the onSettled invalidation in
      // useStartSession, which refetches the active session and wallet so
      // the card reflects the real state.
    }
  }

  if (isPending) return <div className="h-36 animate-pulse rounded-2xl bg-wash" aria-hidden />;

  if (isError) {
    return (
      <section>
        <p className="text-[11.5px] font-medium uppercase tracking-[0.06em] text-ink-3">Instanță</p>
        <p className="mt-2 text-[13.5px] text-bad">Nu s-a putut încărca starea instanței.</p>
      </section>
    );
  }

  const cardState = instanceCardState(active);

  if (cardState === 'provisioning' && active) {
    return (
      <section>
        <div className="flex items-center justify-between gap-3">
          <p className="text-[11.5px] font-medium uppercase tracking-[0.06em] text-ink-3">Instanță</p>
          <Pill variant="warn">Se pornește</Pill>
        </div>
        <p className="mt-2 text-[20px] font-[600] text-ink">Se pornește instanța…</p>
        <p className="mt-1 text-[13.5px] text-ink-3">
          Durează de obicei 2–5 minute · plan {tierLabel(active.tierId)}
          {billing && active.hourlyPriceEur !== undefined && <> · {formatCurrencyEurRo(active.hourlyPriceEur)}/oră</>}
        </p>
      </section>
    );
  }

  if (cardState === 'ready' && active) {
    return (
      <section>
        <div className="flex items-center justify-between gap-3">
          <p className="text-[11.5px] font-medium uppercase tracking-[0.06em] text-ink-3">Instanță</p>
          <Pill variant="accent">Activă</Pill>
        </div>
        {billing && active.costEur !== undefined ? (
          <p className="mt-1 text-[40px] font-[650] leading-none tabular-nums text-ink">{formatCurrencyEurRo(active.costEur)}</p>
        ) : (
          <p className="mt-1 text-[20px] font-[600] text-ink">Instanță activă</p>
        )}
        <p className="mt-2 text-[13.5px] text-ink-3">
          {billing && active.hourlyPriceEur !== undefined && <>{formatCurrencyEurRo(active.hourlyPriceEur)}/oră · </>}
          plan {tierLabel(active.tierId)} · se oprește automat după 30 de minute de inactivitate.
        </p>
        {billing && (
          <>
            <Button className="mt-3" onClick={() => stop.mutate(active.id)} disabled={stop.isPending}>
              {stop.isPending ? <Loader2 className="motion-safe:animate-spin" /> : <Square />}
              {stop.isPending ? 'Se oprește…' : 'Oprește instanța'}
            </Button>
            {stop.isError && <p className="mt-2 text-[13.5px] text-bad">Oprirea a eșuat. Reîncercați.</p>}
          </>
        )}
      </section>
    );
  }

  return (
    <section>
      <div className="flex items-center justify-between gap-3">
        <p className="text-[11.5px] font-medium uppercase tracking-[0.06em] text-ink-3">Instanță</p>
        <Pill variant="mute">Inactivă</Pill>
      </div>
      <p className="mt-2 text-[20px] font-[600] text-ink">Nicio instanță activă</p>
      <p className="mt-1 text-[13.5px] text-ink-3">
        {w ? `~${formatCurrencyEurRo(w.hourlyPriceEur)}/oră estimativ · minim 1 oră` : 'Pornirea consumă credite din portofel.'}
      </p>
      {billing ? (
        <>
          {startError && <p className="mt-3 max-w-md text-[13.5px] text-bad">{startError}</p>}
          <Button className="mt-3" onClick={onStart} disabled={start.isPending}>
            {start.isPending ? <Loader2 className="motion-safe:animate-spin" /> : <Play />}
            {start.isPending ? 'Se pornește…' : 'Pornește instanța'}
          </Button>
        </>
      ) : (
        <p className="mt-3 text-[13.5px] text-ink-3">
          Contactați un administrator al organizației pentru a porni o instanță.
        </p>
      )}
    </section>
  );
}
